<template>
  <section id="testimonials" class="section bg-gray-50 dark:bg-dark-bg relative overflow-hidden">
    <!-- Background Decoration -->
    <div class="absolute top-1/2 left-0 w-1/3 h-1/3 bg-gradient-to-r from-primary/5 to-transparent blur-3xl transform -translate-y-1/2"></div>

    <div class="container-custom">
      <!-- Section Header -->
      <div class="text-center mb-16">
        <h2 class="text-4xl md:text-5xl font-display font-bold text-gray-900 dark:text-white mb-4">
          Client Testimonials
        </h2>
        <p class="text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
          What our clients say about working with us
        </p>
      </div>

      <!-- Testimonials Slider -->
      <div
        class="relative max-w-4xl mx-auto"
        @mouseenter="pauseAutoPlay"
        @mouseleave="resumeAutoPlay"
      >
        <!-- Testimonial Cards -->
        <div class="relative overflow-hidden">
          <div
            class="flex transition-transform duration-300 ease-out"
            :style="{ transform: `translateX(-${currentIndex * 100}%)` }"
          >
            <div
              v-for="(testimonial, index) in testimonials"
              :key="index"
              class="w-full flex-shrink-0 px-4"
            >
              <!-- Testimonial Card -->
              <div class="bg-white dark:bg-dark-surface rounded-2xl p-8 md:p-12 shadow-xl border border-gray-200 dark:border-dark-border">
                <!-- Quote Icon -->
                <div class="flex justify-center mb-6">
                  <div class="w-16 h-16 bg-accent/10 rounded-full flex items-center justify-center">
                    <Quote :size="32" class="text-accent" />
                  </div>
                </div>

                <!-- Quote Text -->
                <p class="text-lg md:text-xl text-gray-700 dark:text-gray-300 text-center mb-8 leading-relaxed italic">
                  "{{ testimonial.quote }}"
                </p>

                <!-- Author Info -->
                <div class="text-center">
                  <p class="font-bold text-gray-900 dark:text-white text-lg">
                    {{ testimonial.author }}
                  </p>
                  <p class="text-sm text-accent font-semibold mt-1">
                    {{ testimonial.company }}
                  </p>
                  <!-- Company Logo -->
                  <div class="mt-6 pt-6 border-t border-gray-200 dark:border-dark-border">
                    <img
                      :src="testimonial.logo"
                      :alt="testimonial.company + ' logo'"
                      class="h-10 max-w-[140px] mx-auto object-contain opacity-70 dark:opacity-90 dark:brightness-0 dark:invert"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Navigation Arrows -->
        <button
          @click="prevSlide"
          class="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 md:-translate-x-12 p-3 bg-white dark:bg-dark-surface rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-primary"
          aria-label="Previous testimonial"
        >
          <ChevronLeft :size="24" class="text-primary dark:text-accent" />
        </button>

        <button
          @click="nextSlide"
          class="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 md:translate-x-12 p-3 bg-white dark:bg-dark-surface rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-primary"
          aria-label="Next testimonial"
        >
          <ChevronRight :size="24" class="text-primary dark:text-accent" />
        </button>

        <!-- Dots Navigation -->
        <div class="flex justify-center mt-8 space-x-2">
          <button
            v-for="(_, index) in testimonials"
            :key="index"
            @click="goToSlide(index)"
            :class="[
              'w-3 h-3 rounded-full transition-all duration-300 focus:outline-none',
              currentIndex === index
                ? 'bg-accent w-8'
                : 'bg-gray-300 dark:bg-gray-600 hover:bg-gray-400'
            ]"
            :aria-label="`Go to testimonial ${index + 1}`"
          ></button>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue';
import { ChevronLeft, ChevronRight, Quote } from 'lucide-vue-next';
import { testimonials } from '@/data/testimonials.js';

const currentIndex = ref(0);
let autoPlayInterval = null;
let isAutoPlayPaused = false;

const nextSlide = () => {
  currentIndex.value = (currentIndex.value + 1) % testimonials.length;
};

const prevSlide = () => {
  currentIndex.value = (currentIndex.value - 1 + testimonials.length) % testimonials.length;
};

const goToSlide = (index) => {
  currentIndex.value = index;
};

const startAutoPlay = () => {
  autoPlayInterval = setInterval(() => {
    if (!isAutoPlayPaused) {
      nextSlide();
    }
  }, 5000);
};

const stopAutoPlay = () => {
  if (autoPlayInterval) {
    clearInterval(autoPlayInterval);
    autoPlayInterval = null;
  }
};

const pauseAutoPlay = () => {
  isAutoPlayPaused = true;
};

const resumeAutoPlay = () => {
  isAutoPlayPaused = false;
};

onMounted(() => {
  startAutoPlay();
});

onUnmounted(() => {
  stopAutoPlay();
});
</script>
